/**
 * Organization entitlements — multi-membership model.
 *
 * Tiers (mutually exclusive within each group, combinable across groups):
 *   • Base:     voice_of_customer | market_intelligence | full_intelligence
 *   • Survey:   survey | survey_plus | survey_pro | survey_pro_plus
 *   • Add-ons:  customer_journey, pivony_sonic, advisor, industry_expert, …
 *
 * Reads `subscriptionRow.entitlements[]` from the API when present (new model),
 * otherwise derives bundled products from the legacy single PackageId row.
 */

export const PRODUCT = {
  VOICE_OF_CUSTOMER: "voice_of_customer",
  MARKET_INTELLIGENCE: "market_intelligence",
  FULL_INTELLIGENCE: "full_intelligence",
  SURVEY: "survey",
  SURVEY_PLUS: "survey_plus",
  SURVEY_PRO: "survey_pro",
  SURVEY_PRO_PLUS: "survey_pro_plus",
  SURVEY_ENTERPRISE: "survey_enterprise",
  CUSTOMER_JOURNEY: "customer_journey",
  CUSTOMER_365: "customer_365",
  ADVANCED_BEHAVIORAL_SEGMENTATION: "advanced_behavioral_segmentation",
  PIVONY_SONIC: "pivony_sonic",
  SONIC_ENGAGE_ACTIVATION: "sonic_engage_activation",
  SONIC_ENGAGE_ENTERPRISE: "sonic_engage_enterprise",
  SONIC_PROSPECT: "sonic_prospect",
  SONIC_PROSPECT_PLUS: "sonic_prospect_plus",
  SONIC_PROSPECT_PRO: "sonic_prospect_pro",
  SONIC_PROSPECT_PRO_PLUS: "sonic_prospect_pro_plus",
  SONIC_PROSPECT_ENTERPRISE: "sonic_prospect_enterprise",
  SONIC_ADVISOR_ENTERPRISE: "sonic_advisor_enterprise",
};

/** Products where at most one should be active per org. */
export const EXCLUSIVE_TIERS = {
  base: [
    PRODUCT.VOICE_OF_CUSTOMER,
    PRODUCT.MARKET_INTELLIGENCE,
    PRODUCT.FULL_INTELLIGENCE,
  ],
  survey: [
    PRODUCT.SURVEY,
    PRODUCT.SURVEY_PLUS,
    PRODUCT.SURVEY_PRO,
    PRODUCT.SURVEY_PRO_PLUS,
    PRODUCT.SURVEY_ENTERPRISE,
  ],
};

const LEGACY_VOC_PACKAGE_IDS = [100, 110, 120];
const LEGACY_CUSTOMER_JOURNEY_PACKAGE_IDS = [110];
const LEGACY_PIVONY_SONIC_PACKAGE_IDS = [120];
const LEGACY_MARKET_INTELLIGENCE_PACKAGE_IDS = [200];
const LEGACY_FULL_INTELLIGENCE_PACKAGE_IDS = [300];
const LEGACY_SURVEY_PLUS_PACKAGE_IDS = [115];

const NAME_MARKERS = {
  [PRODUCT.VOICE_OF_CUSTOMER]: ["voice of customer", "voc"],
  [PRODUCT.MARKET_INTELLIGENCE]: ["market intelligence"],
  [PRODUCT.FULL_INTELLIGENCE]: ["full intelligence", "full plan"],
  [PRODUCT.SURVEY_PLUS]: ["survey plus", "survey+"],
  [PRODUCT.CUSTOMER_JOURNEY]: ["customer journey"],
  [PRODUCT.PIVONY_SONIC]: ["pivony sonic", "voc + pivony sonic"],
};

function packageNameMatches(productKey, packageName) {
  const markers = NAME_MARKERS[productKey] || [];
  return markers.some((marker) => packageName.includes(marker));
}

function isActiveSubscription(subscriptionRow) {
  if (!subscriptionRow) return false;
  return subscriptionRow.IsActive !== false;
}

/** Derive product keys from the legacy single PackageId / PackageName row. */
export function deriveLegacyEntitlements(subscriptionRow) {
  const products = new Set();
  if (!isActiveSubscription(subscriptionRow)) return products;

  const packageId = Number(subscriptionRow.PackageId);
  const packageName = String(subscriptionRow.PackageName || "")
    .trim()
    .toLowerCase();

  const addIf = (productKey, byId, byName = true) => {
    if (Number.isFinite(packageId) && byId.includes(packageId)) {
      products.add(productKey);
      return;
    }
    if (byName && packageName && packageNameMatches(productKey, packageName)) {
      products.add(productKey);
    }
  };

  addIf(PRODUCT.VOICE_OF_CUSTOMER, LEGACY_VOC_PACKAGE_IDS);
  addIf(PRODUCT.MARKET_INTELLIGENCE, LEGACY_MARKET_INTELLIGENCE_PACKAGE_IDS);
  addIf(PRODUCT.FULL_INTELLIGENCE, LEGACY_FULL_INTELLIGENCE_PACKAGE_IDS);

  // Any active org gets base survey; Survey Plus is an upgrade within the survey tier.
  products.add(PRODUCT.SURVEY);
  addIf(PRODUCT.SURVEY_PLUS, LEGACY_SURVEY_PLUS_PACKAGE_IDS);

  addIf(PRODUCT.CUSTOMER_JOURNEY, LEGACY_CUSTOMER_JOURNEY_PACKAGE_IDS);
  addIf(PRODUCT.PIVONY_SONIC, LEGACY_PIVONY_SONIC_PACKAGE_IDS);

  return products;
}

/** Resolve all active product keys for an org subscription snapshot. */
export function resolveEntitlements(subscriptionRow) {
  if (!subscriptionRow) return new Set();

  if (
    Array.isArray(subscriptionRow.entitlements) &&
    subscriptionRow.entitlements.length > 0
  ) {
    const products = new Set();
    subscriptionRow.entitlements.forEach((entry) => {
      if (!entry) return;
      const status = entry.status;
      if (status !== "active" && status !== "trial") return;
      const key = entry.product_key || entry.productKey;
      if (key) products.add(key);
    });
    if (products.has(PRODUCT.SURVEY_ENTERPRISE)) {
      products.add(PRODUCT.SURVEY_PRO_PLUS);
      products.add(PRODUCT.SURVEY_PRO);
      products.add(PRODUCT.SURVEY_PLUS);
      products.add(PRODUCT.SURVEY);
    } else if (products.has(PRODUCT.SURVEY_PRO_PLUS)) {
      products.add(PRODUCT.SURVEY_PRO);
      products.add(PRODUCT.SURVEY_PLUS);
      products.add(PRODUCT.SURVEY);
    } else if (products.has(PRODUCT.SURVEY_PRO)) {
      products.add(PRODUCT.SURVEY_PLUS);
      products.add(PRODUCT.SURVEY);
    } else if (products.has(PRODUCT.SURVEY_PLUS)) {
      products.add(PRODUCT.SURVEY);
    }
    if (products.has(PRODUCT.SONIC_PROSPECT_ENTERPRISE)) {
      products.add(PRODUCT.SONIC_PROSPECT_PRO_PLUS);
      products.add(PRODUCT.SONIC_PROSPECT_PRO);
      products.add(PRODUCT.SONIC_PROSPECT_PLUS);
      products.add(PRODUCT.SONIC_PROSPECT);
    }
    if (products.has(PRODUCT.SONIC_ENGAGE_ENTERPRISE)) {
      products.add(PRODUCT.SONIC_ENGAGE_ACTIVATION);
      products.add(PRODUCT.PIVONY_SONIC);
    }
    // Migrated orgs send a non-empty array. Empty/canceled-only → legacy PackageId.
    if (products.size > 0) {
      return products;
    }
  }

  return deriveLegacyEntitlements(subscriptionRow);
}

export function hasProduct(subscriptionRow, productKey) {
  return resolveEntitlements(subscriptionRow).has(productKey);
}

export function getActiveTierProduct(subscriptionRow, tierName) {
  const tier = EXCLUSIVE_TIERS[tierName];
  if (!tier) return null;
  const active = resolveEntitlements(subscriptionRow);
  return tier.find((productKey) => active.has(productKey)) || null;
}

export function hasVoiceOfCustomerPlan(subscriptionRow) {
  return hasProduct(subscriptionRow, PRODUCT.VOICE_OF_CUSTOMER);
}

export function hasMarketIntelligencePlan(subscriptionRow) {
  return hasProduct(subscriptionRow, PRODUCT.MARKET_INTELLIGENCE);
}

export function hasFullIntelligencePlan(subscriptionRow) {
  return hasProduct(subscriptionRow, PRODUCT.FULL_INTELLIGENCE);
}

export function hasSurveyPlan(subscriptionRow) {
  return hasProduct(subscriptionRow, PRODUCT.SURVEY);
}

/**
 * What a product's console offers: everything ("active"), a read-only view of what the org
 * built while it had the plan ("read_only": the trial or plan has ended), or nothing ("none").
 * The API sends SurveyAccess / SonicEngageAccess with subscriptions/all and refuses writes by
 * the same rule, so its flag wins; without it (an older API) the plan decides.
 */
export const PLAN_ACCESS = { ACTIVE: "active", READ_ONLY: "read_only", NONE: "none" };

const accessFlag = (value) => (Object.values(PLAN_ACCESS).includes(value) ? value : null);

export function surveyAccess(subscriptionRow) {
  return (
    accessFlag(subscriptionRow?.SurveyAccess) ||
    (hasSurveyPlan(subscriptionRow) ? PLAN_ACCESS.ACTIVE : PLAN_ACCESS.NONE)
  );
}

export function hasSurveyPlusPlan(subscriptionRow) {
  return (
    hasProduct(subscriptionRow, PRODUCT.SURVEY_PLUS) ||
    hasProduct(subscriptionRow, PRODUCT.SURVEY_PRO) ||
    hasProduct(subscriptionRow, PRODUCT.SURVEY_PRO_PLUS) ||
    hasProduct(subscriptionRow, PRODUCT.SURVEY_ENTERPRISE)
  );
}

export function hasSurveyProPlan(subscriptionRow) {
  return (
    hasProduct(subscriptionRow, PRODUCT.SURVEY_PRO) ||
    hasProduct(subscriptionRow, PRODUCT.SURVEY_PRO_PLUS) ||
    hasProduct(subscriptionRow, PRODUCT.SURVEY_ENTERPRISE)
  );
}

export function hasSurveyProPlusPlan(subscriptionRow) {
  return (
    hasProduct(subscriptionRow, PRODUCT.SURVEY_PRO_PLUS) ||
    hasProduct(subscriptionRow, PRODUCT.SURVEY_ENTERPRISE)
  );
}

export function hasSurveyEnterprisePlan(subscriptionRow) {
  return hasProduct(subscriptionRow, PRODUCT.SURVEY_ENTERPRISE);
}

export function hasSurveyPaidPlan(subscriptionRow) {
  return hasSurveyPlusPlan(subscriptionRow);
}

export function hasCustomerJourneyPlan(subscriptionRow) {
  return hasProduct(subscriptionRow, PRODUCT.CUSTOMER_JOURNEY);
}

export function hasPivonySonicPlan(subscriptionRow) {
  return hasSonicEngagePlan(subscriptionRow);
}

export function hasSonicEngagePlan(subscriptionRow) {
  return (
    hasProduct(subscriptionRow, PRODUCT.PIVONY_SONIC) ||
    hasProduct(subscriptionRow, "pivony_sonic_activation") ||
    hasProduct(subscriptionRow, PRODUCT.SONIC_ENGAGE_ACTIVATION) ||
    hasProduct(subscriptionRow, PRODUCT.SONIC_ENGAGE_ENTERPRISE)
  );
}

export function sonicEngageAccess(subscriptionRow) {
  return (
    accessFlag(subscriptionRow?.SonicEngageAccess) ||
    (hasSonicEngagePlan(subscriptionRow) ? PLAN_ACCESS.ACTIVE : PLAN_ACCESS.NONE)
  );
}

/** One plan: any Sonic Prospect entitlement (older tier keys included) opens every feature. */
export function hasSonicProspectPlan(subscriptionRow) {
  return (
    hasProduct(subscriptionRow, PRODUCT.SONIC_PROSPECT) ||
    hasProduct(subscriptionRow, PRODUCT.SONIC_PROSPECT_PLUS) ||
    hasProduct(subscriptionRow, PRODUCT.SONIC_PROSPECT_PRO) ||
    hasProduct(subscriptionRow, PRODUCT.SONIC_PROSPECT_PRO_PLUS) ||
    hasProduct(subscriptionRow, PRODUCT.SONIC_PROSPECT_ENTERPRISE)
  );
}

export function hasAbsAccess(subscriptionRow) {
  return hasProduct(
    subscriptionRow,
    PRODUCT.ADVANCED_BEHAVIORAL_SEGMENTATION
  );
}
